import type { ReactElement, ReactNode } from "react";
import type { Belegvorschau } from "@kundeninfo/api-client";

/**
 * Das BLATT (Spec, Oberfläche, Mitte): die Seitenbilder der Engine — das exakte Blatt,
 * nicht eine Nachbildung. Zoom „Breite" (in die Spalte) oder „100 %" (Bildpunkte wie
 * gerendert). Hilfslinien zeigen die Ränder des Entwurfs als Überlagerung — rein
 * clientseitig, sie gehen nie ins PDF. `ueberlagerung` zeichnet je Seite weitere Rahmen
 * (die Grafiken, K10).
 */

export type BlattProps = {
  vorschau: Belegvorschau | null;
  laedt: boolean;
  fehler: string | null;
  zoom: "breite" | "voll";
  hilfslinien: boolean;
  raender: { oben: number; rechts: number; unten: number; links: number };
  ueberlagerung?: ((seite: number) => ReactNode) | undefined;
};

function prozent(mm: number, von: number): string {
  return `${(mm / von) * 100}%`;
}

export function LayoutBlatt(p: BlattProps): ReactElement {
  return (
    <div className={`lay__blatt${p.laedt ? " lay__blatt--laedt" : ""}`} aria-busy={p.laedt}>
      {p.fehler !== null && <p className="lay__blattfehler" role="alert">{p.fehler}</p>}
      {p.vorschau === null && p.fehler === null && (
        <p className="lay__blattleer">
          {p.laedt ? "Das Blatt wird gerendert …"
            : "Noch keine Vorschau — „Aktualisieren“ rendert das Blatt."}
        </p>
      )}
      {(p.vorschau?.seiten ?? []).map((seite) => (
        <figure key={seite.nummer} className="lay__seite"
          style={p.zoom === "voll" ? { width: `${seite.breite}px` } : undefined}>
          <img className="lay__seitenbild" src={`data:image/png;base64,${seite.png}`}
            width={seite.breite} height={seite.hoehe} alt={`Seite ${seite.nummer}`}
            draggable={false} />
          {p.hilfslinien && (
            <div className="lay__hilfslinien" aria-hidden="true" style={{
              top: prozent(p.raender.oben, 297), right: prozent(p.raender.rechts, 210),
              bottom: prozent(p.raender.unten, 297), left: prozent(p.raender.links, 210),
            }} />
          )}
          {p.ueberlagerung?.(seite.nummer)}
        </figure>
      ))}
    </div>
  );
}
